import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  HouseSimple,
  Files,
  ArrowRight,
  CheckCircle,
  Clock,
  CalendarBlank,
  PenNib,
  FileText,
  Warning,
  CurrencyEur,
  ChatText,
} from '@phosphor-icons/react';
import { Skeleton } from '../../components/ui/skeleton';
import Glossaire from '../client/Glossaire';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RP69-72 (MOA-A1, D30) — « Mon chantier » : l'accueil du client (MOA), pensé
 * d'abord pour le téléphone (une colonne). Une seule lecture serveur :
 * GET /api/chantiers/{id}/mon-chantier (registre public de 09-VOCABULAIRE).
 *   - Où en est-on : étapes, avancement, fin prévue, retard, message de l'architecte ;
 *   - Ce qu'on attend de vous : modifications du marché à accepter, signatures en
 *     attente (PV de réception…), prochain rendez-vous de chantier ;
 *   - Dernier compte rendu (diffusé seulement) et documents partagés ;
 *   - Paiements : seulement si l'architecte l'a activé (D31) ;
 *   - Glossaire (MOA-A10).
 * Un client qui a plusieurs chantiers choisit le sien ; A5 / P4#9 — par défaut s'ouvre
 * le chantier qui attend quelque chose de lui (GET /api/chantiers/{id}/attentes-client :
 * avenant à accepter, signature, paiement à régler).
 */

const fmtDate = (v) => {
  if (!v) return '';
  const d = new Date(String(v).length === 10 ? `${v}T12:00:00` : v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};
const fmtDateHeure = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};
const fmtEur = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;

const ETAT_ETAPE = {
  terminee: 'bg-[#E8F5E9] text-[#2D6A4F]',
  en_cours: 'bg-[#FFF8E1] text-[#B8860B] font-bold',
  a_venir: 'bg-[#F5F5F3] text-[#9A9A9A]',
};

function Carte({ titre, icon: Icon, children, testid, action }) {
  return (
    <section className="border border-[#E0E0DB] bg-white" data-testid={testid}>
      <div className="px-4 py-3 border-b border-[#E0E0DB] flex items-center gap-2">
        {Icon && <Icon size={18} className="text-[#C75B39]" />}
        <h2 className="flex-1 text-sm font-bold uppercase tracking-wider text-[#6B6B6B]">{titre}</h2>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

export default function DashboardClient({ user }) {
  const [chantiers, setChantiers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [erreur, setErreur] = useState(false);
  const [attentes, setAttentes] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data: list } = await axios.get(`${API_URL}/api/chantiers`, { withCredentials: true });
        const arr = Array.isArray(list) ? list : [];
        setChantiers(arr);
        if (arr.length === 1) setSelectedId(arr[0].id);
        if (arr.length > 1) {
          // P4#9 — ouvrir le chantier qui a une action en attente (sinon le premier)
          const res = await Promise.allSettled(arr.map(c => axios.get(
            `${API_URL}/api/chantiers/${c.id}/attentes-client`, { withCredentials: true })));
          const parId = {};
          res.forEach((r, i) => { if (r.status === 'fulfilled') parId[arr[i].id] = r.value.data; });
          setAttentes(parId);
          const score = (c) => ((parId[c.id]?.nb_a_faire || 0) * 10) + (parId[c.id]?.nb_a_payer || 0);
          const prioritaire = arr.reduce((best, c) => (score(c) > score(best) ? c : best), arr[0]);
          setSelectedId(prioritaire.id);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    let vivant = true;
    setLoadingData(true);
    setErreur(false);
    axios.get(`${API_URL}/api/chantiers/${selectedId}/mon-chantier`, { withCredentials: true })
      .then(({ data: d }) => { if (vivant) setData(d); })
      .catch(() => { if (vivant) { setData(null); setErreur(true); } })
      .finally(() => { if (vivant) setLoadingData(false); });
    return () => { vivant = false; };
  }, [selectedId]);

  if (loading) return <div className="space-y-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>;

  const prenom = (user?.name || '').split(' ')[0];

  if (chantiers.length === 0) {
    return (
      <div className="text-center py-12 border border-[#E0E0DB] bg-white" data-testid="dashboard-client">
        <HouseSimple size={40} className="mx-auto text-[#9A9A9A] mb-3" />
        <p className="text-[#6B6B6B]">Aucun chantier pour le moment</p>
        <p className="text-sm text-[#9A9A9A]">Votre architecte vous invitera bientôt</p>
      </div>
    );
  }

  const o = data?.ou_en_est;
  const a = data?.attentes;
  const cr = data?.dernier_compte_rendu;
  const pay = data?.paiements;

  return (
    <div className="space-y-4 max-w-3xl mx-auto" data-testid="dashboard-client">
      <div data-testid="mon-chantier" className="space-y-4">
        {/* En-tête + choix du chantier */}
        <div className="border border-[#E0E0DB] bg-white p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <HouseSimple size={28} className="text-[#C75B39] flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-[#2C2C2C]" style={{ fontFamily: "'DM Serif Display', serif" }}>
                Bonjour {prenom}
              </h1>
              <p className="text-sm text-[#6B6B6B]">Mon chantier</p>
            </div>
          </div>
          {chantiers.length > 1 && (
            <label className="block mt-4" data-testid="dashboard-client-projects">
              <span className="text-xs text-[#6B6B6B]">Choisir mon chantier</span>
              <select
                value={selectedId || ''}
                onChange={e => setSelectedId(e.target.value)}
                className="mt-1 w-full border border-[#E0E0DB] bg-white px-3 py-2 text-sm"
                data-testid="mon-chantier-choix"
              >
                {chantiers.map(c => {
                  const n = (attentes[c.id]?.nb_a_faire || 0) + (attentes[c.id]?.nb_a_payer || 0);
                  return <option key={c.id} value={c.id}>{c.name || 'Chantier'}{n > 0 ? ` — ${n} à faire` : ''}</option>;
                })}
              </select>
            </label>
          )}
          {data?.chantier && (
            <div className="mt-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-lg font-bold text-[#2C2C2C]">{data.chantier.nom}</p>
                {data.chantier.adresse && <p className="text-sm text-[#6B6B6B]">{data.chantier.adresse}</p>}
              </div>
              <Link to={`/chantiers/${data.chantier.id}`} className="text-sm text-[#C75B39] hover:underline flex items-center gap-1 flex-shrink-0">
                Tout voir <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </div>

        {loadingData && <Skeleton className="h-40" />}
        {erreur && !loadingData && (
          <p className="text-sm text-[#8B3A3A] border border-[#E0E0DB] bg-white p-4">
            Impossible d'afficher ce chantier pour le moment. Réessayez dans quelques instants.
          </p>
        )}

        {data && !loadingData && (
          <>
            {/* RP69 — Où en est-on */}
            <Carte titre="Où en est-on" icon={Clock} testid="mon-chantier-ou-en-est">
              {o.phase && (
                <p className="text-sm text-[#2C2C2C]">Étape actuelle : <strong>{o.phase}</strong></p>
              )}
              {o.etapes.length > 0 && (
                <ol className="grid grid-cols-2 sm:grid-cols-3 gap-1 mt-3">
                  {o.etapes.map(e => (
                    <li key={e.libelle} className={`text-center p-2 text-xs ${ETAT_ETAPE[e.etat] || ETAT_ETAPE.a_venir}`}>
                      {e.etat === 'terminee' && <CheckCircle size={14} className="mx-auto mb-1" weight="fill" />}
                      {e.etat === 'en_cours' && <Clock size={14} className="mx-auto mb-1" />}
                      {e.libelle}
                    </li>
                  ))}
                </ol>
              )}
              {o.planning_publie ? (
                <div className="mt-4 space-y-3">
                  {o.avancement_pct !== null && o.avancement_pct !== undefined && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-[#6B6B6B]">Avancement des travaux</span>
                        <span className="text-lg font-bold text-[#1E3A5F]">{Math.round(o.avancement_pct)} %</span>
                      </div>
                      <div className="w-full h-3 bg-[#E0E0DB] overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#1E3A5F] to-[#C75B39]" style={{ width: `${Math.min(100, o.avancement_pct)}%` }} />
                      </div>
                    </div>
                  )}
                  {o.fin_prevue && (
                    <p className="text-sm text-[#2C2C2C]">
                      Fin des travaux prévue le <strong>{fmtDate(o.fin_prevue)}</strong>
                      {o.retard_jours > 0 && (
                        <span className="text-[#C75B39]"> ({o.retard_jours} jours de retard sur le planning prévu)</span>
                      )}
                    </p>
                  )}
                  {o.retards?.length > 0 && (
                    <p className="text-sm text-[#6B6B6B] flex items-start gap-2">
                      <Warning size={16} className="text-[#C75B39] flex-shrink-0 mt-0.5" />
                      <span>Retard sur : {o.retards.map(r => `${r.lot} (+${r.jours} j)`).join(', ')}</span>
                    </p>
                  )}
                  {o.message_architecte && (
                    <blockquote className="border-l-4 border-[#C75B39] bg-[#FAFAF8] px-3 py-2 text-sm text-[#2C2C2C]">
                      <span className="block text-xs text-[#6B6B6B] mb-1">Le mot de votre architecte</span>
                      {o.message_architecte}
                    </blockquote>
                  )}
                  {o.jalons.length > 0 && (
                    <ul className="text-sm space-y-1">
                      {o.jalons.map(j => (
                        <li key={`${j.nom}-${j.date}`} className="flex justify-between gap-3">
                          <span className="text-[#2C2C2C]">{j.nom}</span>
                          <span className="text-[#6B6B6B] whitespace-nowrap">{fmtDate(j.date)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <p className="mt-3 text-sm text-[#9A9A9A]">Le planning est en cours de préparation par votre architecte.</p>
              )}
            </Carte>

            {/* RP70 — Ce qu'on attend de vous */}
            <Carte titre="Ce qu'on attend de vous" icon={PenNib} testid="mon-chantier-attentes">
              {a.nb_a_faire === 0 && (
                <p className="text-sm text-[#2D6A4F] flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" /> Rien à faire pour l'instant.
                </p>
              )}
              <ul className="space-y-3">
                {a.signatures.map(s => (
                  <li key={s.id} className="border border-[#E0E0DB] p-3" data-testid="attente-signature">
                    <p className="text-sm font-semibold text-[#2C2C2C]">{s.libelle}</p>
                    <p className="text-xs text-[#6B6B6B]">{s.titre}{s.expire_le && ` · avant le ${fmtDate(s.expire_le)}`}</p>
                    <a href={s.lien} className="mt-2 inline-flex items-center gap-1 bg-[#C75B39] text-white text-sm px-3 py-2" data-testid="attente-signer">
                      Lire et signer <ArrowRight size={14} />
                    </a>
                  </li>
                ))}
                {a.avenants.map(av => (
                  <li key={av.id} className="border border-[#E0E0DB] p-3" data-testid="attente-avenant">
                    <p className="text-sm font-semibold text-[#2C2C2C]">Modification du marché à accepter : {av.titre}</p>
                    {av.motif && <p className="text-sm text-[#4A4A4A]">{av.motif}</p>}
                    <p className="text-xs text-[#6B6B6B]">
                      {av.lots.length > 0 && `${av.lots.join(', ')} · `}
                      {av.montant_ht !== null && av.montant_ht !== undefined && `${av.montant_ht >= 0 ? '+' : ''}${fmtEur(av.montant_ht)} HT · `}
                      {av.statut_libelle}
                    </p>
                    <Link to={`/chantiers/${data.chantier.id}`} className="mt-2 inline-flex items-center gap-1 bg-[#C75B39] text-white text-sm px-3 py-2" data-testid="attente-avenant-decider">
                      Accepter ou refuser <ArrowRight size={14} />
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-start gap-2 text-sm" data-testid="attente-rendez-vous">
                <CalendarBlank size={18} className="text-[#1E3A5F] flex-shrink-0" />
                {a.prochain_rendez_vous ? (
                  <p>
                    <span className="text-[#6B6B6B]">Prochain rendez-vous de chantier : </span>
                    <strong>{fmtDateHeure(a.prochain_rendez_vous.date) || fmtDate(a.prochain_rendez_vous.date)}</strong>
                    {a.prochain_rendez_vous.lieu && <span className="text-[#6B6B6B]"> · {a.prochain_rendez_vous.lieu}</span>}
                  </p>
                ) : (
                  <p className="text-[#9A9A9A]">Rendez-vous de chantier : aucun prévu pour l'instant.</p>
                )}
              </div>
            </Carte>

            {/* RP71 — Dernier compte rendu + documents (une colonne au téléphone) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Carte titre="Dernier compte rendu" icon={ChatText} testid="mon-chantier-dernier-cr">
                {cr ? (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-[#2C2C2C]">{cr.titre}</p>
                    <p className="text-xs text-[#6B6B6B]">{fmtDate(cr.diffuse_le || cr.date)}</p>
                    {cr.resume && <p className="text-sm text-[#4A4A4A]">{cr.resume}</p>}
                    {cr.accord_avant_le && (
                      <p className="text-xs text-[#6B6B6B]">
                        Si vous ne signalez rien avant le {fmtDate(cr.accord_avant_le)}, ce compte rendu sera considéré comme accepté.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-3 pt-1">
                      <Link to={`/chantiers/${data.chantier.id}?tab=suivi&sub=crc`} className="text-sm text-[#C75B39] hover:underline">
                        Lire le compte rendu
                      </Link>
                      {cr.nb_comptes_rendus > 1 && (
                        <span className="text-xs text-[#9A9A9A] self-center">{cr.nb_comptes_rendus} comptes rendus au total</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-[#9A9A9A]">Votre architecte n'a pas encore diffusé de compte rendu.</p>
                )}
              </Carte>

              <Carte titre="Documents partagés" icon={Files} testid="mon-chantier-documents">
                {data.documents.length === 0 ? (
                  <p className="text-sm text-[#9A9A9A]">Aucun document partagé avec vous pour l'instant</p>
                ) : (
                  <ul className="divide-y divide-[#E0E0DB] -my-2">
                    {data.documents.map(doc => (
                      <li key={doc.id}>
                        <Link to={`/chantiers/${data.chantier.id}?tab=documents`} className="py-2 flex items-center gap-3 hover:bg-[#FAFAF8]">
                          <FileText size={18} className="text-[#1E3A5F] flex-shrink-0" />
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm text-[#2C2C2C] truncate">{doc.nom}</span>
                            {doc.date && <span className="block text-xs text-[#6B6B6B]">{fmtDate(doc.date)}</span>}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </Carte>
            </div>

            {/* RP73 / D31 — Paiements, seulement si l'architecte l'a activé */}
            {pay?.visible && (
              <Carte
                titre="Paiements"
                icon={CurrencyEur}
                testid="mon-chantier-paiements"
                action={<Link to={`/chantiers/${data.chantier.id}?tab=paiements`} className="text-xs text-[#C75B39] hover:underline">Détail</Link>}
              >
                <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div><dt className="text-xs text-[#6B6B6B]">Déjà payé (HT)</dt><dd className="font-bold text-[#2D6A4F]">{fmtEur(pay.deja_paye_ht)}</dd></div>
                  <div><dt className="text-xs text-[#6B6B6B]">À payer (TTC)</dt><dd className="font-bold text-[#C75B39]">{fmtEur(pay.a_payer_prochainement_ttc)}</dd></div>
                  <div><dt className="text-xs text-[#6B6B6B]">Reste à payer (HT)</dt><dd className="font-bold text-[#2C2C2C]">{fmtEur(pay.reste_a_payer_ht)}</dd></div>
                </dl>
              </Carte>
            )}

            {/* RP72 — glossaire intégré */}
            <Glossaire />
          </>
        )}
      </div>
    </div>
  );
}
